import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { searchPaymentByFolio, refundPayment, partialRefundPayment, sendReceiptByEmail } from '../api/pos.api.js';
import { usePrinterStore }   from '../store/printerStore.js';
import { useAuthStore }      from '../store/authStore.js';
import { thermalPrinter }    from '../native/thermalPrinter.js';
import { IcoReturn, IcoPrint, IcoCheck, IcoEmail } from '../components/common/Icons.jsx';
import styles from './RefundScreen.module.css';

const fmt     = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const METHOD_LABEL = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', mixed: 'Mixto' };

export default function RefundScreen() {
  const navigate      = useNavigate();
  const { state }     = useLocation();
  const printerDevId  = usePrinterStore((s) => s.deviceId);
  const printerTrans  = usePrinterStore((s) => s.transport);
  const user          = useAuthStore((s) => s.user);

  const [folio,       setFolio]       = useState('');
  const [payment,     setPayment]     = useState(null);
  const [searching,   setSearching]   = useState(false);
  const [confirming,  setConfirming]  = useState(false);
  const [done,        setDone]        = useState(false);
  const [error,       setError]       = useState(null);
  const [selected,    setSelected]    = useState({});
  const [reason,      setReason]      = useState('');
  const [printing,    setPrinting]    = useState(false);
  const [emailOpen,   setEmailOpen]   = useState(false);
  const [emailVal,    setEmailVal]    = useState('');
  const [sending,     setSending]     = useState(false);
  const [sendOk,      setSendOk]      = useState(false);

  // Pre-load payment when navigated from SalesHistory
  useEffect(() => {
    if (!state?.payment) return;
    const p = state.payment;
    setPayment(p);
    setFolio((p.client_uuid ?? p._id).slice(-8).toUpperCase());
    const alreadyRefunded = {};
    for (const ri of p.refund_items ?? []) {
      const key = String(ri.product_id ?? ri.name);
      alreadyRefunded[key] = (alreadyRefunded[key] || 0) + ri.quantity;
    }
    const initial = {};
    (p.items ?? []).forEach((item, i) => {
      const key   = String(item.product_id ?? item.name);
      const avail = item.quantity - (alreadyRefunded[key] || 0);
      if (avail > 0) initial[i] = avail;
    });
    setSelected(initial);
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!folio.trim()) return;
    setError(null);
    setPayment(null);
    setSelected({});
    setSearching(true);
    try {
      const p = await searchPaymentByFolio(folio.trim());
      setPayment(p);
      // pre-select all remaining (not yet refunded) items
      const alreadyRefunded = {};
      for (const ri of p.refund_items ?? []) {
        const key = String(ri.product_id ?? ri.name);
        alreadyRefunded[key] = (alreadyRefunded[key] || 0) + ri.quantity;
      }
      const initial = {};
      (p.items ?? []).forEach((item, i) => {
        const key   = String(item.product_id ?? item.name);
        const avail = item.quantity - (alreadyRefunded[key] || 0);
        if (avail > 0) initial[i] = avail;
      });
      setSelected(initial);
    } catch (err) {
      setError(err.response?.data?.error ?? 'No se encontró ninguna venta con ese folio');
    } finally {
      setSearching(false);
    }
  };

  // remaining quantity per item index
  const calcRemaining = (p) => {
    const already = {};
    for (const ri of p?.refund_items ?? []) {
      const key = String(ri.product_id ?? ri.name);
      already[key] = (already[key] || 0) + ri.quantity;
    }
    return (p?.items ?? []).map((item) => {
      const key = String(item.product_id ?? item.name);
      return item.quantity - (already[key] || 0);
    });
  };

  const remaining = payment ? calcRemaining(payment) : [];

  const toggleItem = (i) => {
    if (remaining[i] <= 0) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[i]) delete next[i];
      else next[i] = remaining[i];
      return next;
    });
  };

  const setQty = (i, val) => {
    const v = Math.max(1, Math.min(remaining[i], Number(val) || 1));
    setSelected((prev) => ({ ...prev, [i]: v }));
  };

  const selectedAmount = (payment?.items ?? []).reduce(
    (sum, item, i) => sum + (selected[i] ?? 0) * item.unit_price, 0,
  );

  const refundableCount = remaining.filter((r) => r > 0).length;
  const isFullRefund =
    refundableCount > 0 &&
    Object.keys(selected).length === refundableCount &&
    Object.entries(selected).every(([i, qty]) => qty === remaining[Number(i)]);

  const handleRefund = async () => {
    setError(null);
    setConfirming(true);
    try {
      if (isFullRefund) {
        await refundPayment(payment._id);
      } else {
        const items = (payment.items ?? [])
          .map((item, i) => (selected[i] ? { ...item, quantity: selected[i] } : null))
          .filter(Boolean);
        await partialRefundPayment(payment._id, items, reason || undefined);
      }
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error ?? 'Error al procesar la devolución');
    } finally {
      setConfirming(false);
    }
  };

  const resetAll = () => {
    setDone(false); setPayment(null); setFolio('');
    setSelected({}); setReason(''); setEmailOpen(false); setSendOk(false);
  };

  const handlePrintRefund = async () => {
    setPrinting(true);
    try {
      if (printerDevId && !thermalPrinter.isConnected()) {
        await thermalPrinter.autoReconnect(printerDevId, printerTrans).catch(() => {});
      }
      await thermalPrinter.printReceipt({
        business:  null,
        date:      new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
        cashier:   user?.name ?? user?.email ?? null,
        folio:     (payment?.client_uuid ?? payment?._id ?? '').slice(-8).toUpperCase(),
        items:     (payment?.items ?? [])
          .filter((_, i) => selected[i])
          .map((item, i) => ({
            name:       `↩ ${item.name}`,
            quantity:   selected[i] ?? item.quantity,
            unit_price: item.unit_price,
          })),
        subtotal:  selectedAmount,
        discount:  0,
        total:     selectedAmount,
        method:    payment?.method ?? 'cash',
        changeDue: 0,
        note:      `DEVOLUCIÓN${reason ? ' - ' + reason : ''}`,
      });
    } catch {
      // non-critical — printer might not be available
    } finally {
      setPrinting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailVal.trim()) return;
    setSending(true);
    try {
      await sendReceiptByEmail(payment._id, { email: emailVal.trim(), items: payment.items });
      setSendOk(true);
      setEmailOpen(false);
    } catch { /* silent */ } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className={styles.root}>
        <div className={styles.inner}>
          <div className={styles.successIcon}><IcoReturn size={28} /></div>
          <h1 className={styles.successTitle}>Devolución procesada</h1>
          <p className={styles.successSub}>
            Folio <span className={styles.folioTag}>{payment?.client_uuid?.slice(-8).toUpperCase()}</span>
          </p>
          {payment?.method === 'cash' && (
            <div className={styles.cashReturn}>
              <span>Devolver al cliente</span>
              <strong>{fmt(selectedAmount || payment?.total)}</strong>
            </div>
          )}

          {/* Print / email actions */}
          <div className={styles.receiptActions}>
            <button className="btn btn--ghost btn--full" onClick={handlePrintRefund} disabled={printing}>
              {printing ? <span className="spinner" /> : <><IcoPrint size={14} /> Imprimir comprobante</>}
            </button>
            {sendOk ? (
              <p className={styles.sendOkMsg}><IcoCheck size={13} /> Comprobante enviado por email</p>
            ) : emailOpen ? (
              <div className={styles.emailInline}>
                <input
                  className="input"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={emailVal}
                  autoFocus
                  onChange={(e) => setEmailVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSendEmail(); if (e.key === 'Escape') setEmailOpen(false); }}
                />
                <button className="btn btn--primary btn--full" onClick={handleSendEmail} disabled={sending}>
                  {sending ? <span className="spinner" /> : 'Enviar'}
                </button>
              </div>
            ) : (
              <button className="btn btn--ghost btn--full" onClick={() => setEmailOpen(true)}>
                <IcoEmail size={14} /> Enviar por email
              </button>
            )}
          </div>

          <div className={styles.doneActions}>
            <button className="btn btn--primary btn--full" onClick={() => navigate('/', { replace: true })}>
              Volver al catálogo
            </button>
            <button className="btn btn--ghost btn--full" onClick={resetAll}>
              Nueva devolución
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.inner}>

        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Devolución</h1>
        </div>

        {/* Búsqueda por folio */}
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <label className={styles.label}>Folio del ticket</label>
          <div className={styles.searchRow}>
            <input
              className="input"
              placeholder="Ej. A1B2C3D4"
              value={folio}
              maxLength={36}
              onChange={(e) => setFolio(e.target.value.toUpperCase())}
              style={{ textTransform: 'uppercase', fontFamily: 'var(--f-mono)', fontSize: 18, letterSpacing: 2 }}
              autoFocus
            />
            <button
              type="submit"
              className="btn btn--primary"
              disabled={searching || !folio.trim()}
              style={{ flexShrink: 0 }}
            >
              {searching ? <span className="spinner" /> : 'Buscar'}
            </button>
          </div>
          <p className={styles.hint}>El folio aparece en el ticket impreso (últimos 8 caracteres)</p>
        </form>

        {error && <p className={styles.error}>{error}</p>}

        {/* Detalle de la venta encontrada */}
        {payment && (
          <div className={styles.paymentCard}>
            <div className={styles.cardHeader}>
              <span className={styles.cardFolio}>{payment.client_uuid.slice(-8).toUpperCase()}</span>
              <span className={`${styles.statusBadge} ${
                payment.status === 'refunded'           ? styles.badgeRefunded :
                payment.status === 'partially_refunded' ? styles.badgePartial  :
                styles.badgeCompleted
              }`}>
                {payment.status === 'refunded'           ? 'Devuelta' :
                 payment.status === 'partially_refunded' ? 'Parcial'  :
                 'Completada'}
              </span>
            </div>

            <div className={styles.cardMeta}>
              <span>{fmtDate(payment.createdAt)}</span>
              <span>{METHOD_LABEL[payment.method] ?? payment.method}</span>
            </div>

            {/* Selección de ítems para devolución */}
            {payment.status !== 'refunded' && payment.items?.length > 0 && (
              <>
                <p className={styles.itemsLabel}>Selecciona qué devolver:</p>
                <div className={styles.items}>
                  {payment.items.map((item, i) => {
                    const rem        = remaining[i] ?? 0;
                    const isSelected = !!selected[i];
                    const selQty     = selected[i] ?? 0;
                    return (
                      <div
                        key={i}
                        className={`${styles.item} ${rem === 0 ? styles.itemDone : ''} ${isSelected ? styles.itemSelected : ''}`}
                        onClick={() => rem > 0 && toggleItem(i)}
                      >
                        <div className={styles.itemCheck}>
                          {rem === 0
                            ? <span className={styles.checkDone}><IcoCheck size={13} /></span>
                            : <span className={`${styles.checkbox} ${isSelected ? styles.checkboxOn : ''}`} />}
                        </div>
                        <div className={styles.itemInfo}>
                          <span className={styles.itemName}>
                            {item.quantity > 1 && <span className={styles.qty}>{item.quantity}×</span>}
                            {item.name}
                          </span>
                          {rem === 0 && <span className={styles.alreadyTag}>Ya devuelto</span>}
                        </div>
                        <div className={styles.itemRight}>
                          {isSelected && item.quantity > 1 && rem > 1 && (
                            <input
                              className={styles.qtyInput}
                              type="number"
                              min={1}
                              max={rem}
                              value={selQty}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setQty(i, e.target.value)}
                            />
                          )}
                          <span className={styles.itemPrice}>{fmt(item.quantity * item.unit_price)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {Object.keys(selected).length > 0 && (
                  <div className={styles.refundSummary}>
                    <span className={styles.refundLabel}>{isFullRefund ? 'Devolución total' : 'Devolución parcial'}</span>
                    <strong className={styles.refundAmount}>{fmt(selectedAmount)}</strong>
                  </div>
                )}

                <input
                  className="input"
                  placeholder="Motivo (opcional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ marginTop: 8, fontSize: 14 }}
                />

                <button
                  className="btn btn--danger btn--full"
                  onClick={handleRefund}
                  disabled={confirming || Object.keys(selected).length === 0}
                  style={{ marginTop: 12 }}
                >
                  {confirming
                    ? <span className="spinner" />
                    : `Confirmar devolución de ${fmt(selectedAmount)}`}
                </button>
              </>
            )}

            {payment.status === 'refunded' && (
              <p className={styles.alreadyRefunded}>Esta venta ya fue devuelta en su totalidad.</p>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
